/**
 * Product data structure for schema generation
 */
export interface ProductSchemaInput {
  name: string;
  description: string;
  imageUrl: string | string[];
  brand?: string;
  sku?: string;
  mpn?: string;
  gtin?: string;
  category?: string;
  // Price and availability. Omit `price` when the page has no single
  // visible price; the Product is then emitted without an Offer.
  price?: number;
  currency: string;
  country: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'Discontinued';
  priceValidUntil?: string; // ISO date format
  url: string;
  // Ratings and reviews
  rating?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
  // Reviews (optional)
  reviews?: Array<{
    author: string;
    datePublished: string;
    reviewBody: string;
    ratingValue: number;
  }>;
  // Additional product info
  color?: string;
  material?: string;
  weight?: string;
  dimensions?: {
    length: string;
    width: string;
    height: string;
  };
  // Variants (for products like Ring Gen 1/2/3 or different sizes)
  variants?: Array<{
    name: string;
    sku: string;
    url: string;
    price: number;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'Discontinued';
  }>;
}

/**
 * Generates Product schema for a single product
 * Handles country-specific pricing and currency
 */
export function generateProductSchema(input: ProductSchemaInput) {
  const {
    name,
    description,
    imageUrl,
    brand = 'Ultrahuman',
    sku,
    mpn,
    gtin,
    category,
    price,
    currency,
    country,
    availability = 'InStock',
    priceValidUntil,
    url,
    rating,
    reviews,
    color,
    material,
    weight,
    dimensions,
  } = input;

  // Omit the Offer when the caller has no single visible price.
  const offer =
    typeof price === 'number'
      ? {
          '@type': 'Offer',
          url,
          priceCurrency: currency,
          price: price.toString(),
          availability: `https://schema.org/${availability}`,
          priceValidUntil:
            priceValidUntil ||
            new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0], // Default 90 days
          seller: {
            '@type': 'Organization',
            name: 'Ultrahuman',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: country,
            },
          },
        }
      : undefined;

  // Build aggregate rating if provided
  const aggregateRating = rating
    ? {
        '@type': 'AggregateRating',
        ratingValue: rating.ratingValue.toString(),
        reviewCount: rating.reviewCount.toString(),
        bestRating: (rating.bestRating || 5).toString(),
        worstRating: (rating.worstRating || 1).toString(),
      }
    : undefined;

  // Build reviews if provided
  const reviewsSchema = reviews?.map((review) => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.datePublished,
    reviewBody: review.reviewBody,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.ratingValue.toString(),
      bestRating: '5',
      worstRating: '1',
    },
  }));

  const productSchema: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: Array.isArray(imageUrl) ? imageUrl : [imageUrl],
    brand: {
      '@type': 'Brand',
      name: brand,
    },
    sku,
    mpn,
    gtin,
    category,
    ...(offer ? { offers: offer } : {}),
    url,
  };

  // Add optional fields
  if (aggregateRating) {
    productSchema.aggregateRating = aggregateRating;
  }

  if (reviewsSchema && reviewsSchema.length > 0) {
    productSchema.review = reviewsSchema;
  }

  if (color) {
    productSchema.color = color;
  }

  if (material) {
    productSchema.material = material;
  }

  if (weight) {
    productSchema.weight = weight;
  }

  if (dimensions) {
    productSchema.depth = dimensions.length;
    productSchema.width = dimensions.width;
    productSchema.height = dimensions.height;
  }

  return productSchema;
}

/**
 * Generates Product schema with variants (e.g., Ring Gen 1/2/3, different sizes)
 * This creates a ProductGroup schema with individual product variants
 */
export function generateProductSchemaWithVariants(
  baseInput: ProductSchemaInput,
) {
  if (!baseInput.variants || baseInput.variants.length === 0) {
    return [generateProductSchema(baseInput)];
  }

  // Generate schema for each variant
  return baseInput.variants.map((variant) => {
    return generateProductSchema({
      ...baseInput,
      name: variant.name,
      sku: variant.sku,
      url: variant.url,
      price: variant.price,
      availability: variant.availability,
    });
  });
}

/**
 * Converts the schema object to JSON-LD string for injection
 */
export function getProductSchemaScript(input: ProductSchemaInput): string {
  return JSON.stringify(generateProductSchema(input));
}

/**
 * Converts multiple product schemas to JSON-LD string for injection
 */
export function getProductSchemaScriptWithVariants(
  input: ProductSchemaInput,
): string {
  const schemas = generateProductSchemaWithVariants(input);
  return JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
}
